/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary, consistent-return */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axiosInstance from '../_helpers/interceptor';
import { DEV_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';

type Subscription = {
  id: number;
  clientId: number;
  planName: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  paypalOrderId: string;
};

type Payment = {
  id: number;
  subscriptionId: number;
  paypalPaymentId: string;
  paypalPayerId: string;
  amount: number;
  currency: string;
  status: string;
  paymentDate: string | null;
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadge(status: string): string {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED')
    return 'bg-success-500/15 text-success-400 border-success-500/30';
  if (s === 'PENDING') return 'bg-warning-500/15 text-warning-300 border-warning-500/30';
  if (s === 'FAILED') return 'bg-error-500/15 text-error-400 border-error-500/30';
  if (s === 'EXPIRED') return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
  return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
}

export default function AdminPaymentsPage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const [subsRes, paysRes] = await Promise.all([
          axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/admin/subscriptions`),
          axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/admin/payments`)
        ]);
        if (cancelled) return;
        setSubscriptions(subsRes.data || []);
        setPayments(paysRes.data || []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load admin data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAllowed]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const totalCollected = payments
    .filter((p) => (p.status || '').toLowerCase() === 'approved')
    .reduce((acc, p) => acc + (p.amount || 0), 0);

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payments & Subscriptions (Admin)</h1>
          <p className="text-sm text-gray-400">Signed in as {userEmail}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Subscriptions</div>
            <div className="text-xl font-semibold">{subscriptions.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Payments</div>
            <div className="text-xl font-semibold">{payments.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Total Collected</div>
            <div className="text-xl font-semibold">${totalCollected.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-icons-outlined animate-spin">autorenew</span>
          Loading…
        </div>
      ) : error ? (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {error}
        </div>
      ) : (
        <>
          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">Subscriptions</h2>
              <span className="text-sm text-gray-400">{subscriptions.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-500 text-left text-gray-400">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Plan</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Start</th>
                    <th className="px-4 py-2">End</th>
                    <th className="px-4 py-2">PayPal Order</th>
                  </tr>
                </thead>
                <tbody>
                  {[...subscriptions]
                    .sort((a, b) => b.id - a.id)
                    .map((s) => (
                      <tr key={s.id} className="border-t border-gray-800">
                        <td className="px-4 py-2 font-mono">{s.id}</td>
                        <td className="px-4 py-2">{s.clientId}</td>
                        <td className="px-4 py-2">{s.planName}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-xs ${statusBadge(
                              s.status
                            )}`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{formatDate(s.startDate)}</td>
                        <td className="px-4 py-2">{formatDate(s.endDate)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {s.paypalOrderId || '—'}
                        </td>
                      </tr>
                    ))}
                  {subscriptions.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                        No subscriptions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">Payments</h2>
              <span className="text-sm text-gray-400">{payments.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-500 text-left text-gray-400">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Subscription</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">PayPal Payment</th>
                    <th className="px-4 py-2">Payer</th>
                  </tr>
                </thead>
                <tbody>
                  {[...payments]
                    .sort((a, b) => b.id - a.id)
                    .map((p) => (
                      <tr key={p.id} className="border-t border-gray-800">
                        <td className="px-4 py-2 font-mono">{p.id}</td>
                        <td className="px-4 py-2">{p.subscriptionId}</td>
                        <td className="px-4 py-2">
                          {p.currency} {p.amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-xs ${statusBadge(
                              p.status
                            )}`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {p.paypalPaymentId}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {p.paypalPayerId}
                        </td>
                      </tr>
                    ))}
                  {payments.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                        No payments
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
